import { Card, Colors, Icon, RadioGroup, Text } from '@amelio/ui-library';
import { useId } from 'react';

import {
    Feedback360SettingFixedTile,
    Feedback360SettingTile,
    type Feedback360SettingMeaning,
} from './Feedback360SettingTile';

import type { IconProps } from '@amelio/ui-library';

import { LockNotice } from '@/components/LockNotice/LockNotice';
import {
    cycleCardRadius,
    cycleCardShadow,
    cycleRailSurfaceBlur,
    cycleRailSurfaceBorderColor,
    cycleRailSurfaceGradient,
} from '@/utils/cycleStyles';

export interface Feedback360SettingChoice<TValue extends string> {
    value: TValue;
    label: string;
    iconName: IconProps['name'];
    meaning: Feedback360SettingMeaning;
}

export interface Feedback360SettingCardProps<TValue extends string> {
    /** Groups the radios; also prefixes each tile's ids, so it must be unique on the page. */
    name: string;
    title: string;
    description: string;
    iconName: IconProps['name'];
    choices: Feedback360SettingChoice<TValue>[];
    value: TValue;
    onChange?: (value: TValue) => void;
    /** Why the setting can no longer be changed here; absent while the choice is still open. */
    lockNotice?: string;
    /** Why the setting is decided elsewhere: only the value in force shows, with this as its tooltip. */
    fixedReason?: string;
    className?: string;
    dataTestId?: string;
}

/** One setting of a 360, offered as tiles that each say what the choice means for the respondents. */
export const Feedback360SettingCard = <TValue extends string>({
    name,
    title,
    description,
    iconName,
    choices,
    value,
    onChange,
    lockNotice,
    fixedReason,
    className = '',
    dataTestId,
}: Feedback360SettingCardProps<TValue>) => {
    const titleId = useId();

    const findChoice = (choiceValue: string) =>
        choices.find((choice) => choice.value === choiceValue);

    const fixedChoice = fixedReason ? findChoice(value) : undefined;

    return (
        <Card
            className={`${cycleCardRadius} border ${cycleRailSurfaceBlur} ${cycleCardShadow} max-md:p-4 md:p-10 ${className}`}
            padding={'none'}
            bgColor={cycleRailSurfaceGradient}
            borderColor={cycleRailSurfaceBorderColor}
            dataTestId={dataTestId}
        >
            {/* On a phone the icon shares the title's line and what follows takes the card's whole width; from
                `md` the icon keeps a column of its own beside the title and everything under it. */}
            <div
                className={
                    'flex flex-wrap items-center gap-x-3 md:flex-nowrap md:items-start md:gap-x-6'
                }
            >
                <Icon
                    name={iconName}
                    size={'xl'}
                    color={Colors.BASE}
                    ariaHidden={true}
                />

                <div className={'contents md:flex md:min-w-0 md:flex-1 md:flex-col'}>
                    <Text
                        id={titleId}
                        weight={'semibold'}
                    >
                        {title}
                    </Text>

                    <div className={'flex w-full flex-col'}>
                        <Text
                            size={'small'}
                            color={Colors.STEP_RAIL_MUTED}
                            className={'mt-3'}
                        >
                            {description}
                        </Text>

                        {fixedChoice && fixedReason ? (
                            <div className={'mt-6 grid gap-4 sm:grid-cols-2'}>
                                <Feedback360SettingFixedTile
                                    label={fixedChoice.label}
                                    iconName={fixedChoice.iconName}
                                    meaning={fixedChoice.meaning}
                                    reason={fixedReason}
                                    dataTestId={`${name}-fixed-choice`}
                                />
                            </div>
                        ) : (
                            <RadioGroup
                                className={'mt-6'}
                                columns={2}
                                name={name}
                                options={choices.map(({ label, value: choiceValue }) => ({
                                    label,
                                    value: choiceValue,
                                }))}
                                value={value}
                                readOnly={!!lockNotice}
                                ariaLabelledBy={titleId}
                                renderOption={(optionProps) => {
                                    const choice = findChoice(optionProps.option.value);
                                    if (!choice) {
                                        return null;
                                    }

                                    return (
                                        <Feedback360SettingTile
                                            {...optionProps}
                                            iconName={choice.iconName}
                                            meaning={choice.meaning}
                                        />
                                    );
                                }}
                                onChange={(selected) => {
                                    const choice = findChoice(selected);
                                    if (choice) {
                                        onChange?.(choice.value);
                                    }
                                }}
                            />
                        )}

                        {lockNotice && <LockNotice className={'mt-4'}>{lockNotice}</LockNotice>}
                    </div>
                </div>
            </div>
        </Card>
    );
};
